import React, { useState, useEffect, useRef, forwardRef } from 'react';

interface CustomTextareaProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  rows?: number;
  style?: React.CSSProperties;
  ref?: React.ForwardedRef<HTMLTextAreaElement>;
  [key: string]: any; // For other props
}

const CustomTextarea = forwardRef<HTMLTextAreaElement, CustomTextareaProps>(
  ({ value, onChange, className, placeholder, rows = 3, ...props }, ref) => {
    // Internal state to handle input
    const [internalValue, setInternalValue] = useState(value || '');
    const internalRef = useRef<HTMLTextAreaElement>(null);
    
    // Forward the ref
    const textareaRef = (ref || internalRef) as React.RefObject<HTMLTextAreaElement>;

    // Update internal state when external value changes
    useEffect(() => {
      setInternalValue(value || '');
      
      // Also directly update DOM element to ensure it's in sync
      if (textareaRef.current && textareaRef.current.value !== value) {
        textareaRef.current.value = value || '';
      }
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      setInternalValue(newValue);
      onChange(newValue);
    };

    return (
      <textarea
        ref={textareaRef}
        value={internalValue}
        onChange={handleChange}
        placeholder={placeholder}
        rows={rows}
        className={className}
        {...props}
      />
    );
  }
);

CustomTextarea.displayName = 'CustomTextarea';

export default CustomTextarea;
